import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import HeaderV2 from '../components/HeaderV2'
import BusinessHeroV2 from './business/v2/BusinessHeroV2'
import BusinessIntroV2 from './business/v2/BusinessIntroV2'
import BusinessSplitBlockV2 from './business/v2/BusinessSplitBlockV2'
import { businessSections } from './business/v2/content'

/**
 * BusinessPageV2 — /{lang}/business/v2
 * Barista Map style "Our Business" page for ONGU Studio.
 *
 * Sections (top → bottom):
 *  1. HeaderV2  (transparent overlay, white text)
 *  2. BusinessHeroV2  (full-bleed image + bottom title)
 *  3. BusinessIntroV2  (square image + Brewing Connections narrative)
 *  4. BusinessSplitBlockV2 × 3
 *      - 門店  → /{lang}/store/xinyi
 *      - 網購  → /{lang}/shop
 *      - 批發  → /{lang}/wholesale
 *  5. FooterV2 is rendered at the App router level.
 */
const BusinessPageV2: React.FC = () => {
  const location = useLocation()
  const currentLang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isPreviewRoute = location.pathname.includes('/v2')

  // Inject baristamap-style fonts (EB Garamond + Noto Serif TC) once per mount
  useEffect(() => {
    const ID = 'ongu-v2-fonts'
    if (document.getElementById(ID)) return
    const link = document.createElement('link')
    link.id = ID
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&display=swap'
    document.head.appendChild(link)
  }, [])

  const seo = useMemo(
    () =>
      currentLang === 'zh'
        ? {
            title: 'Our Business｜ONGU Studio｜門店・網購・批發 (V2 預覽)',
            description:
              'ONGU Studio 的三大事業：信義旗艦門店、精品咖啡豆網購、餐飲與企業 B2B 批發合作。從自家烘焙到職人手沖，把每一杯咖啡的故事好好說完。',
          }
        : {
            title: 'Our Business｜ONGU Studio｜Store・Shop・Wholesale (V2 Preview)',
            description:
              'Three pillars of ONGU Studio: the Xinyi flagship store, our online specialty bean shop, and B2B wholesale partnerships for cafés, restaurants and enterprise clients.',
          },
    [currentLang]
  )

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {isPreviewRoute && <meta name="robots" content="noindex,nofollow" />}
        {isPreviewRoute && (
          <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/business`} />
        )}
      </Helmet>

      <div
        className="bg-[#FAF9F1] text-black"
        style={{ fontFamily: "'Noto Sans TC', 'Helvetica Neue', sans-serif" }}
      >
        {/* Solid white header (default) — sits ABOVE hero, not overlapping */}
        <HeaderV2 />

        {/* Spacer for fixed navbar height (h-20 = 80px) */}
        <div className="h-20" />

        {/* 1. Hero — image block sits below navbar (Barista Map rule) */}
        <BusinessHeroV2 currentLang={currentLang} />

        {/* 2. Brand narrative — Brewing Connections */}
        <BusinessIntroV2 currentLang={currentLang} />

        {/* 3. Three business pillars (alternating image side) */}
        {businessSections.map((section) => (
          <BusinessSplitBlockV2
            key={section.key}
            section={section}
            currentLang={currentLang}
          />
        ))}

        {/* Bottom spacer before FooterV2 */}
        <div className="h-12 md:h-20 bg-[#FAF9F1]" />
      </div>
    </>
  )
}

export default BusinessPageV2
